import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import { TrendingUp, Eye, Users, Wallet } from "lucide-react";

import ChatReviews from "./ChatReviews";

// Animated counter — counts from 0 to `to` when first in view.
const Counter = ({ to, prefix = "", suffix = "", decimals = 0, format = "plain" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => {
    const val = decimals > 0 ? v.toFixed(decimals) : Math.floor(v);
    if (format === "comma") return Number(val).toLocaleString("en-IN");
    return String(val);
  });

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration: 2.1,
      ease: [0.22, 1, 0.36, 1],
    });
    return controls.stop;
  }, [inView, to, mv]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
};

const RESULTS = [
  {
    client: "Founder · Wealth Advisory",
    period: "90-day engagement",
    tags: ["Authority Growth", "Instagram"],
    metrics: [
      { icon: Users, label: "Followers", value: "+184K", delta: "+612%" },
      { icon: Eye, label: "Monthly views", value: "42.6M", delta: "+9.4×" },
      { icon: TrendingUp, label: "Inbound leads", value: "1,240", delta: "+380%" },
    ],
  },
  {
    client: "Business Coach · High-ticket",
    period: "Pro Acquisition · 120 days",
    tags: ["VSL Funnel", "Meta Ads"],
    metrics: [
      { icon: TrendingUp, label: "Qualified calls", value: "312", delta: "+220%" },
      { icon: Wallet, label: "Revenue closed", value: "₹1.42Cr", delta: "+3.1×" },
      { icon: Eye, label: "Ad ROAS", value: "5.8×", delta: "on ₹18.4L spend" },
    ],
  },
  {
    client: "Educator · Career &amp; Skill",
    period: "Elite Webinar Launch",
    tags: ["Webinar", "One-to-many"],
    metrics: [
      { icon: Users, label: "Registrations", value: "8,420", delta: "in 21 days" },
      { icon: TrendingUp, label: "Show-up rate", value: "38.4%", delta: "vs 24% avg" },
      { icon: Wallet, label: "Launch revenue", value: "₹2.86Cr", delta: "single event" },
    ],
  },
];

const OVERVIEW = [
  { label: "Views Accelerated", to: 3, suffix: "B+" },
  { label: "Followers grown", to: 12, suffix: "M+" },
  { label: "Qualified calls booked", to: 85000, suffix: "+", format: "comma" },
  { label: "Client revenue attributed", prefix: "₹", to: 140, suffix: "Cr+" },
];

export default function Results() {
  return (
    <section
      id="results"
      data-testid="results-section"
      className="section-light relative py-16 md:py-24 border-t border-[#e5e2d8]"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
          <span className="eyebrow" data-testid="results-eyebrow">
            <span className="eyebrow-dot" />
            Results
          </span>
          <h2 className="mt-6 font-semibold tracking-tight text-3xl md:text-5xl lg:text-6xl leading-[1.05]">
            <span className="text-gradient">Outcomes we&apos;re accountable for.</span>
          </h2>
          <p className="mt-6 text-[#0a0a0a]/60 text-[15px] max-w-xl mx-auto">
            Followers are lag indicators. We measure booked calls, closed
            revenue and predictable pipeline — the numbers that pay bills.
          </p>
        </div>

        {/* Overview strip — light editorial */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.06] mb-16 md:mb-20"
          data-testid="results-overview"
          style={{
            background: "linear-gradient(180deg, #141414 0%, #0a0a0a 100%)",
            boxShadow:
              "0 40px 80px -30px rgba(10,10,10,0.35), 0 0 0 1px rgba(255,255,255,0.04) inset",
          }}
        >
          {OVERVIEW.map((o) => (
            <div
              key={o.label}
              className="p-6 md:p-8"
              style={{ background: "transparent", borderRight: "1px solid rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
            >
              <div className="text-white font-semibold tracking-tight text-2xl md:text-3xl">
                <Counter
                  to={o.to}
                  prefix={o.prefix || ""}
                  suffix={o.suffix || ""}
                  format={o.format || "plain"}
                />
              </div>
              <div className="mt-1 text-white/50 text-[12.5px] uppercase tracking-wider">
                {o.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Chat reviews — swipeable client screenshots */}
        <ChatReviews />

        {/* Case study dashboards */}
        <p className="mt-4 text-center text-[#0a0a0a]/45 text-[12px]">
          * Full performance decks shared under NDA during the strategy call.
        </p>
      </div>
    </section>
  );
}
